/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Typography, Button, Box } from '@mui/material';
import cn from 'classnames';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { dateUtils } from 'utils';
import { timeWindows } from 'const';
import { useAppointmentActions, useOrganization, useClickOutside, useModal } from 'hooks';
import { CameraIcon, CheckMarkIcon, SubMenuIcon, EventBusyIcon } from 'components/elements/Icons';
import { StatusLabel, NameLabel } from 'components/elements';
import AppointmentCancellationReason from './AppointmentCancellationReasonModal';

import Styles from './styles';

const useStyles = makeStyles(Styles);

function Appointment({
  id,
  online,
  startDateTime,
  endDateTime,
  roomFullNameWithoutFees,
  healthcareServiceName,
  providerName,
  providerPronoun,
  onSelect,
  isConfirmed,
  isPast,
  appointmentStatus,
  patientCancellable,
  isCancellationRequested,
}) {
  const classes = useStyles();
  const { joinOnlineAppointment, confirmAppointment, cancelAppointment } = useAppointmentActions(id);
  const { data: organization } = useOrganization();
  const { t } = useTranslation('labels');
  const isPastAppointment = useMemo(() => dateUtils.isDateBeforeNow(endDateTime), [endDateTime]);
  const [subMenuOpened, setSubMenuOpened] = useState(false);
  const subMenuRef = useRef(null);

  const { handleModal } = useModal();
  const cancelCurrentAppointment = (reason) => () => cancelAppointment(reason).finally(() => handleModal(false));
  const toggleCancellationModal = () =>
    handleModal(
      <div className={classes.appointmentCancellationReasonModal}>
        <AppointmentCancellationReason onCancelAppointment={cancelCurrentAppointment} onClose={() => handleModal(false)} />
      </div>,
      { size: 'small' },
    );

  const startDate = useMemo(() => dateUtils.formatDateTimeString(startDateTime), [startDateTime]);
  const startTime = useMemo(() => dateUtils.formatTimeString(startDateTime), [startDateTime]);
  const endTime = useMemo(() => dateUtils.formatTimeString(endDateTime), [endDateTime]);
  const displayJoinButton = useMemo(
    () => online && dateUtils.isInTimeWindow(startDateTime, endDateTime, organization?.videoJoinWindow, organization?.videoJoinWindow),
    [startDateTime, endDateTime, organization?.videoJoinWindow],
  );
  const displayConfirmButton = useMemo(() => dateUtils.isInTimeWindow(startDateTime, endDateTime, timeWindows.appointmentConfirmation, 0), [startDateTime, endDateTime]);

  const onConfirmAppointment = (e) => {
    e.stopPropagation();
    confirmAppointment();
  };
  const onClickCancelAppointment = (e) => {
    e.stopPropagation();
    toggleCancellationModal(true);
    setSubMenuOpened(!subMenuOpened);
  };
  const onJoinAppointment = (e) => {
    e.stopPropagation();
    joinOnlineAppointment();
  };
  const toggleSubMenu = (e) => {
    e.stopPropagation();
    setSubMenuOpened(!subMenuOpened);
  };

  const submenuButtons = [
    {
      action: onClickCancelAppointment,
      icon: <EventBusyIcon classes={classes.cancelButtonIcon} />,
      color: 'error',
      name: t('cancel'),
      disabled: isCancellationRequested || !patientCancellable,
    },
  ];
  const hasActiveMenuButtons = submenuButtons.filter(({ disabled }) => disabled).length !== submenuButtons.length;

  useClickOutside(subMenuRef, () => setSubMenuOpened(false));

  return (
    <div className={classes.appointment} onClick={onSelect} data-testid="appointment-test-id">
      {subMenuOpened && <div className={classes.subMenuOverlay} />}
      <Box className={cn(classes.infoWrapper, { [classes.pastAppointmentInfoWrapper]: isPast })}>
        <div className={classes.dateWrapper}>
          <Typography className={classes.appointmentStartDate} variant="h6" color="text" component="div" noWrap>
            {startDate}
          </Typography>
          <Typography className={classes.appointmentTime} variant="subtitle2" color="textSecondary" component="span" noWrap>
            {startTime} - {endTime}
          </Typography>
          <StatusLabel status={appointmentStatus} classes={classes.statusLabel} />
        </div>
        <div className={classes.service}>
          <div className={classes.serviceInfoWrapper}>
            <Typography className={classes.healthcareServiceName} variant="body2" color="text" component="span" noWrap>
              {healthcareServiceName}
            </Typography>{' '}
            <Typography className={classes.providerName} variant="body2" color="textSecondary" component="span" noWrap>
              {t('with')}
              <NameLabel fullName={providerName} personalPronoun={providerPronoun} options={{ pronoun: { parentheses: true } }} />
            </Typography>{' '}
            <Typography className={classes.roomFullName} variant="body2" color="textSecondary" component="span" noWrap>
              {roomFullNameWithoutFees}
            </Typography>
          </div>
        </div>
      </Box>
      {!isPast && (
        <Box className={classes.controlsWrapper}>
          {displayJoinButton && !isPastAppointment && (
            <Button className={classes.appointmentJoinButton} onClick={onJoinAppointment} variant="contained">
              <CameraIcon classes={classes.joinButtonIcon} />
              <Typography className={classes.controlButtonText} variant="caption" component="span">
                {t('join')}
              </Typography>
            </Button>
          )}
          {displayConfirmButton && !isPastAppointment && (
            <Button className={cn(classes.appointmentConfirmButton, { [classes.appointmentConfirmedButton]: isConfirmed })} onClick={onConfirmAppointment} variant="outlined">
              <CheckMarkIcon classes={classes.confirmButtonIcon} />
              <Typography className={classes.controlButtonText} variant="caption" component="span">
                {t(`${isConfirmed ? 'confirmed' : 'confirm'}`)}
              </Typography>
            </Button>
          )}
          {!isPastAppointment && hasActiveMenuButtons && (
            <div className={cn(classes.subMenuButton, { [classes.openedSubMenuButton]: subMenuOpened })} onClick={toggleSubMenu} ref={subMenuRef}>
              <SubMenuIcon />
              {subMenuOpened ? (
                <div className={classes.subMenu}>
                  {submenuButtons.map(
                    ({ name, action, color, icon, disabled }) =>
                      !disabled && (
                        <Button className={classes.cancelButton} onClick={action} variant="text" key={name}>
                          {icon}
                          <Typography color={color} variant="body2" component="span">
                            {name}
                          </Typography>
                        </Button>
                      ),
                  )}
                </div>
              ) : null}
            </div>
          )}
        </Box>
      )}
    </div>
  );
}

Appointment.propTypes = {
  id: PropTypes.string.isRequired,
  online: PropTypes.bool.isRequired,
  startDateTime: PropTypes.string.isRequired,
  endDateTime: PropTypes.string.isRequired,
  roomFullNameWithoutFees: PropTypes.string.isRequired,
  providerName: PropTypes.string.isRequired,
  providerPronoun: PropTypes.string,
  healthcareServiceName: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  isConfirmed: PropTypes.bool.isRequired,
  isPast: PropTypes.bool.isRequired,
  appointmentStatus: PropTypes.string,
  patientCancellable: PropTypes.bool,
  isCancellationRequested: PropTypes.bool,
};

Appointment.defaultProps = {
  healthcareServiceName: '',
  appointmentStatus: '',
  providerPronoun: '',
  patientCancellable: false,
  isCancellationRequested: false,
};

export default Appointment;
